import { makeAutoObservable, runInAction } from "mobx";
import axios from "axios";
import { apiUrl } from "../config";
import { datePlusDays, datesDiff, getDateString } from "../lib/utils";

const placeholders = {
  sign: "Куда вы хотите поехать?",
  entry: "Регион, город или отель",
  more: "Или ещё поищем?",
};

const defCityUrl = {
  url: "all",
  name: "во всех городах",
};

const defRegionUrl = {
  url: "all",
  name: "в России",
};

const defHotel = {
  _id: null,
  id: null,
  name: "",
  address: [],
  template: "Отель",
  additional_childs: 4,
  dog_friendly: false,
};

const routes = {
  SEARCH_HOTELS: "/search/hotels",
};

class searchStore {
  // Search properties
  location = "";
  currentPage = "index";
  searchOnPage = false;
  placeholder = placeholders.sign;
  query = "";
  isOnFocus = false;
  timer = null;
  regions = [];
  cities = [];
  places = [];
  hotels = [];
  isSearching = false; // Флаг загрузки
  searchRequestId = 0; // ID последнего запроса

  // App properties used in component
  selectedRegion = defRegionUrl;
  selectedCity = defCityUrl;
  selectedPlace = "";
  selectedHotel = defHotel;
  hotelsCount = 12022;
  hotelsTotalCount = 38219;
  searchNum = 0;
  searchMenuOpened = false;
  isLogged = false;
  userData = { role: 0 };

  infoPopupOpened = false;
  infoPopupData = {};

  // Date properties
  dateToday = getDateString(new Date());
  dateCheckin = "";
  dateCheckout = "";
  dateMinCheckout = datePlusDays(new Date(), 1);
  dateDays = 0;

  // Guests properties
  guestsCount = 2;
  guestsChildren = [];
  dogFriendlyAvailable = false; // доступно с собакой
  dogFriendly = false; // едем с собакой

  constructor() {
    makeAutoObservable(this);
    this.initializeFromStorage(); // Вызываем при создании
  }

  // Новый метод для загрузки из localStorage
  initializeFromStorage() {
    // Загружаем даты
    const storedDateIn = localStorage.getItem("datein");
    const storedDateOut = localStorage.getItem("dateout");

    if (storedDateIn) {
      this.dateCheckin = storedDateIn;
      this.dateMinCheckout = datePlusDays(new Date(storedDateIn), 1);
    }

    if (storedDateOut) {
      this.dateCheckout = storedDateOut;
      if (storedDateIn) {
        this.dateDays = datesDiff(storedDateIn, storedDateOut);
      }
    }

    // Загружаем гостей
    const storedGuests = localStorage.getItem("guests");
    if (storedGuests) {
      this.guestsCount = parseInt(storedGuests, 10);
    }

    // Загружаем детей
    const storedChilds = localStorage.getItem("childs");
    if (storedChilds) {
      try {
        const childs = JSON.parse(storedChilds);
        if (Array.isArray(childs)) {
          this.guestsChildren = childs;
        }
      } catch (e) {
        console.error("Error parsing childs from localStorage:", e);
        this.guestsChildren = [];
      }
    }

    // Загружаем dogfriendly
    const storedDogFriendly = localStorage.getItem("dogfriendly");
    if (storedDogFriendly !== null) {
      this.dogFriendly = storedDogFriendly === "true";
    }

    // Загружаем выбранный регион
    const storedRegion = localStorage.getItem("region");
    if (storedRegion) {
      try {
        const region = JSON.parse(storedRegion);
        if (region && region.url) {
          this.selectedRegion = region;
        }
      } catch (e) {
        console.error("Error parsing region from localStorage:", e);
      }
    }

    // Загружаем выбранный город
    const storedCity = localStorage.getItem("city");
    if (storedCity) {
      try {
        const city = JSON.parse(storedCity);
        if (city && city.url) {
          this.selectedCity = city;
        }
      } catch (e) {
        console.error("Error parsing city from localStorage:", e);
      }
    }

    // Загружаем выбранное место
    const storedPlace = localStorage.getItem("place");
    if (storedPlace) {
      try {
        const place = JSON.parse(storedPlace);
        if (place && place.name) {
          this.selectedPlace = place;
        }
      } catch (e) {
        console.error("Error parsing place from localStorage:", e);
      }
    }

    // Загружаем количество отелей
    const storedHotelsCount = localStorage.getItem("hotels_count");
    if (storedHotelsCount) {
      this.hotelsCount = parseInt(storedHotelsCount, 10);
    }

    const storedHotelsTotalCount = localStorage.getItem("hotels_total_count");
    if (storedHotelsTotalCount) {
      this.hotelsTotalCount = parseInt(storedHotelsTotalCount, 10);
    }
  }

  // Date methods
  setDateCheckin(date) {
    this.dateCheckin = date;
    this.dateMinCheckout = datePlusDays(new Date(date), 1);
    localStorage.setItem("datein", date);
    document.cookie = `datein=${date};path=/`;
    // если уже указана дата выезда, обновить кол-во дней
    if (this.dateCheckout) {
      // если дату заезда удалили
      if (!date) {
        this.dateCheckout = "";
        this.dateDays = 0;
        localStorage.setItem("dateout", "");
        document.cookie = `dateout=;path=/`;
      } else {
        this.dateDays = datesDiff(date, this.dateCheckout);
      }
      localStorage.setItem("datedays", date);
    }
  }

  setDateCheckout(date) {
    if (date) {
      this.dateCheckout = date;
      this.dateDays = datesDiff(this.dateCheckin, date);
      localStorage.setItem("dateout", date);
      document.cookie = `dateout=${date};path=/`;
    } else {
      this.dateCheckout = "";
      this.dateDays = 0;
      localStorage.setItem("dateout", "");
      document.cookie = `dateout=;path=/`;
    }
  }

  clearCheckDates() {
    this.dateCheckin = "";
    this.dateCheckout = "";
    document.cookie = `datein=;path=/`;
    document.cookie = `dateout=;path=/`;
  }

  // Guests methods
  setGuestsCount(count) {
    this.guestsCount = +count;
    localStorage.setItem("guests", +count);
    document.cookie = `guests=${count};path=/`;
  }

  setDogFriendlyAvailable(flag) {
    this.dogFriendlyAvailable = flag == "true" || flag == true ? true : false;
  }

  setDogFriendly(flag) {
    this.dogFriendly = flag == "true" || flag == true ? true : false;
    localStorage.setItem("dogfriendly", this.dogFriendly);
    document.cookie = `dogfriendly=${this.dogFriendly};path=/`;
  }

  setGuestChildren(childs = []) {
    this.guestsChildren = childs;
    const jsonChilds = JSON.stringify(childs);
    localStorage.setItem("childs", jsonChilds);
    document.cookie = `childs=${jsonChilds};path=/`;
  }

  addGuestsChildren(children) {
    this.guestsChildren.push(+children);
    const jsonChilds = JSON.stringify(this.guestsChildren);
    localStorage.setItem("childs", jsonChilds);
    document.cookie = `childs=${jsonChilds};path=/`;
  }

  delGuestsChildren(children) {
    this.guestsChildren.splice(+children, 1);
    const jsonChilds = JSON.stringify(this.guestsChildren);
    localStorage.setItem("childs", jsonChilds);
    document.cookie = `childs=${jsonChilds};path=/`;
  }

  // Search methods
  setLocation(value) {
    this.location = value;
    this.calcCurrentPage();
  }

  calcCurrentPage() {
    if (this.location === "/") {
      this.setCurrentPage("index");
    } else if (/^\/russia\/[a-z]+(-[a-z]+)*$/i.test(this.location)) {
      this.setCurrentPage("region");
    } else if (/\/booking\/[a-z0-9]+\/[0-9]+\/[0-9]+$/.test(this.location)) {
      this.setCurrentPage("booking");
    } else if (/\/russia\/[a-z-]+\/[a-z-]+$/.test(this.location)) {
      this.setCurrentPage("city");
    } else if (/\/russia\/[a-z-]+\/[a-z-]+\/[a-z-]+$/.test(this.location)) {
      this.setCurrentPage("place");
    } else if (
      /\/russia\/[a-z-]+\/[a-z-]+\/hotels\/[0-9]+$/.test(this.location)
    ) {
      this.setCurrentPage("hotel");
    }
  }

  setSearchOnPage(b) {
    this.searchOnPage = b;
  }

  setCurrentPage(value) {
    this.currentPage = value;
    this.calcSearchValues();
  }

  calcSearchValues = () => {
    let actual_placeholder = placeholders["sign"];
    switch (this.currentPage) {
      case "index":
        break;
      case "region":
        actual_placeholder = this.selectedRegion?.name;
        break;
      case "city":
        actual_placeholder = this.selectedCity?.name;
        break;
      case "place":
        actual_placeholder = this.selectedPlace?.name;
        break;
      case "hotel":
        actual_placeholder = this.selectedHotel?.name;
        break;
      case "booking":
        break;
      default:
        break;
    }
    this.setPlaceholder(actual_placeholder);
  };

  setQuery = (q) => {
    this.query = q;
    if (q?.length > 2) {
      this.clearTimer();
      this.setTimer(
        setTimeout(() => {
          this.getHotelsList(q); // Передаем запрос напрямую
        }, 300), // Уменьшим задержку для более быстрого отклика
      );
    } else if (q === "") {
      this.clearLists();
      this.clearTimer();
      this.isSearching = false;
    } else {
      // Если меньше 3 символов, очищаем списки
      this.clearLists();
      this.isSearching = false;
    }
  };

  setPlaceholder(value) {
    this.placeholder = value;
  }

  setIsOnFocus = (value) => {
    this.isOnFocus = !!value;
  };

  onFocusHandler = () => {
    this.setIsOnFocus(true);
    this.setPlaceholder(placeholders["entry"]);
    if (this.currentPage == "hotel") {
      this.setQuery("");
    }
  };

  onBlurHandler = () => {
    setTimeout(() => {
      this.setIsOnFocus(false);
    }, 200); // Уменьшим задержку
    this.calcSearchValues();
  };

  onCloseHandler = () => {
    this.setIsOnFocus(false);
    this.calcSearchValues();
  };

  onChangeQuery = (e) => {
    const q = e.currentTarget.value;
    this.setQuery(q);
  };

  getHotelsList = async (searchQuery) => {
    const requestId = ++this.searchRequestId; // Увеличиваем ID запроса
    this.isSearching = true;

    try {
      const res = await axios.post(apiUrl + routes.SEARCH_HOTELS, {
        query: searchQuery,
        // region: this.selectedRegion.url,
        // city: this.selectedCity.url,
        region: "",
        city: "",
        place: this.selectedPlace?.name || "",
      });

      // Проверяем, что это последний запрос
      if (requestId === this.searchRequestId && res.data?.status === "ok") {
        runInAction(() => {
          this.hotels = res.data.data.hotels || [];
          this.regions = res.data.data.regions || [];
          this.cities = res.data.data.cities || [];
          this.places = res.data.data.places || [];
          this.isSearching = false;
        });
      }
    } catch (error) {
      if (requestId === this.searchRequestId) {
        runInAction(() => {
          this.clearLists();
          this.isSearching = false;
        });
      }
    }
  };

  setTimer = (t) => {
    this.timer = t;
  };

  clearTimer = () => {
    clearTimeout(this.timer);
  };

  clearLists = () => {
    this.regions = [];
    this.cities = [];
    this.places = [];
    this.hotels = [];
  };

  // App methods used in component
  setSelectedRegion(url, name, item = true) {
    if (url) {
      this.selectedRegion = { url, name: url !== "all" ? `${name}` : name };
    } else {
      this.selectedRegion = defRegionUrl;
      this.hotelsCount = 12022;
      localStorage.setItem("hotels_count", 12022);
      this.setHotelsTotalCount(38219);
      url = defRegionUrl.url;
      name = defRegionUrl.name;
    }
    this.selectedCity = defCityUrl;

    if (item) {
      localStorage.setItem("region", JSON.stringify({ url, name }));
      localStorage.setItem("city", "");
    }
  }

  setSelectedCity(url, name, item = true) {
    if (!url) {
      this.selectedCity = defCityUrl;
    } else {
      this.selectedCity = { url, name: url !== "all" ? `${name}` : name };
      if (item) {
        localStorage.setItem("city", JSON.stringify({ url, name }));
      }
    }
  }

  setSelectedPlace(item, save = true) {
    this.selectedPlace = item;
    if (save && item?.name) {
      localStorage.setItem(
        "place",
        JSON.stringify({ url: item?.place_url || "", name: item?.name || "" }),
      );
    }
  }

  setHotelsCount(count, item = true) {
    this.hotelsCount = count;
    if (item) {
      localStorage.setItem("hotels_count", count);
    }
  }

  setHotelsTotalCount(count, item = true) {
    this.hotelsTotalCount = count;
    if (item) {
      localStorage.setItem("hotels_total_count", count);
    }
  }

  setSearchNum(searchNum) {
    this.searchNum = searchNum;
  }

  setSearchMenuOpened(opened) {
    this.searchMenuOpened = opened ? true : false;
  }

  setIsLogged(isLogged) {
    this.isLogged = isLogged;
  }

  setUserdata(data) {
    this.userData = data;
  }

  // окно информации
  openInfoPopup = (data = {}) => {
    this.infoPopupOpened = true;
    this.infoPopupData = { ...data };
  };
  closeInfoPopup = () => {
    this.infoPopupOpened = false;
    this.infoPopupData = {};
  };
}

export default new searchStore();
